import React from 'react';
import ModuleRoute from '../routing/ModuleRoute';
import PageBoundary from '../routing/PageBoundary';
import { confirmUnsavedChanges } from '../../hooks/useDraftRecovery';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    AppBar,
    Box,
    Drawer,
    List,
    Toolbar,
    Typography,
    IconButton,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Paper,
    Chip,
    Backdrop,
    CircularProgress,
    Collapse,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Tooltip,
    Divider,
    InputLabel,
} from '@mui/material';
import { Menu as MenuIcon, Logout, Lock, LockOpen, ExpandMore, Search, Warning as WarningIcon, Business as BusinessIcon, Close as CloseIcon, Remove as RemoveIcon, OpenInFull as OpenInFullIcon, CropFree as CropFreeIcon, ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon } from '@mui/icons-material';
import apiService from '../../services/api.service';
import { showSnackbar } from '../../store/slices/uiSlice';
import { AppDispatch, RootState } from '../../store';
import { logout, getProfile } from '../../store/slices/authSlice';
import { toggleSidebar } from '../../store/slices/uiSlice';
import MODULES, { MENU_GROUPS } from '../../config/moduleRegistry';
import SearchResults from '../common/SearchResults';

const DRAWER_WIDTH = 240;
const WINDOW_TOP = 72;
const WINDOW_MARGIN = 20;
const WINDOW_MIN_WIDTH = 420;
const WINDOW_MIN_HEIGHT = 260;
const FOOTER_HEIGHT = 38;

interface MainLayoutProps {
    children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: RootState) => state.auth);
    const { sidebarOpen } = useSelector((state: RootState) => state.ui);
    const WINDOWS_KEY = 'app_windows_v1';
    const ACTIVE_WINDOW_KEY = 'app_active_window_v1';
    const [windows, setWindows] = React.useState<{ id: number; key: string; title: string; minimized: boolean }[]>(() => {
        try { const raw = localStorage.getItem(WINDOWS_KEY); return raw ? JSON.parse(raw) : []; } catch { return []; }
    });
    const [windowPositions, setWindowPositions] = React.useState<Record<string, { x: number; y: number }>>(() => {
        try { return JSON.parse(localStorage.getItem('app_window_positions_v1') || '{}'); } catch { return {}; }
    });
    const [windowSizes, setWindowSizes] = React.useState<Record<string, { width: number; height: number }>>(() => {
        try { return JSON.parse(localStorage.getItem('app_window_sizes_v1') || '{}'); } catch { return {}; }
    });
    const [maximizedWindows, setMaximizedWindows] = React.useState<Record<string, boolean>>(() => {
        try { return JSON.parse(localStorage.getItem('app_window_maximized_v1') || '{}'); } catch { return {}; }
    });
    const [isMobile, setIsMobile] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
    const dragRef = React.useRef<{ id: number; offsetX: number; offsetY: number } | null>(null);
    const resizeRef = React.useRef<{ id: number; startX: number; startY: number; startWidth: number; startHeight: number; startLeft: number; startTop: number; direction: string } | null>(null);
    const autoHiddenSidebarRef = React.useRef<number | null>(null);
    const [activeWindowId, setActiveWindowId] = React.useState<number | null>(() => {
        try { const raw = localStorage.getItem(ACTIVE_WINDOW_KEY); return raw ? Number(raw) : null; } catch { return null; }
    });
    const [companies, setCompanies] = React.useState<{ CompanyID: number; CompanyName: string; DatabaseName?: string }[]>([]);
    const [selectedCompanyId, setSelectedCompanyId] = React.useState<number | ''>(() => {
        try { return Number(localStorage.getItem('selectedCompanyId') || '') as any; } catch { return ''; }
    });
    const [companyLocked, setCompanyLocked] = React.useState(true);
    const [pendingCompanyId, setPendingCompanyId] = React.useState<string | null>(null);
    const [showCompanyChangeConfirm, setShowCompanyChangeConfirm] = React.useState(false);
    const [globalSearchOpen, setGlobalSearchOpen] = React.useState(false);
    const [globalSearchQuery, setGlobalSearchQuery] = React.useState('');
    const [showSearchResults, setShowSearchResults] = React.useState(false);

    React.useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    React.useEffect(() => {
        const onMove = (event: MouseEvent) => {
            const resize = resizeRef.current;
            if (resize && !isMobile) {
                const dx = event.clientX - resize.startX;
                const dy = event.clientY - resize.startY;
                const leftEdge = resize.direction.includes('w');
                const topEdge = resize.direction.includes('n');
                const rightEdge = resize.direction.includes('e');
                const bottomEdge = resize.direction.includes('s');
                let width = resize.startWidth + (rightEdge ? dx : leftEdge ? -dx : 0);
                let height = resize.startHeight + (bottomEdge ? dy : topEdge ? -dy : 0);
                let x = resize.startLeft + (leftEdge ? dx : 0);
                let y = resize.startTop + (topEdge ? dy : 0);
                const minLeft = sidebarOpen ? DRAWER_WIDTH : 0;
                const maxRight = window.innerWidth - WINDOW_MARGIN;
                const maxBottom = window.innerHeight - FOOTER_HEIGHT - WINDOW_MARGIN;
                x = Math.max(minLeft, Math.min(x, window.innerWidth - WINDOW_MIN_WIDTH));
                if (leftEdge) width = resize.startLeft + resize.startWidth - x;
                width = Math.max(WINDOW_MIN_WIDTH, Math.min(width, maxRight - x));
                height = Math.max(WINDOW_MIN_HEIGHT, Math.min(height, maxBottom - y));
                if (leftEdge) x = Math.max(minLeft, Math.min(x, resize.startLeft + resize.startWidth - WINDOW_MIN_WIDTH));
                if (topEdge) y = Math.max(56, Math.min(y, resize.startTop + resize.startHeight - WINDOW_MIN_HEIGHT));
                setWindowSizes((prev) => ({
                    ...prev,
                    [String(resize.id)]: {
                        width,
                        height,
                    },
                }));
                setWindowPositions((prev) => ({ ...prev, [String(resize.id)]: { x, y } }));
                return;
            }
            const drag = dragRef.current;
            if (!drag || isMobile) return;
            const currentSize = windowSizes[String(drag.id)];
            const width = currentSize?.width ?? Math.min(Math.max(WINDOW_MIN_WIDTH, window.innerWidth - (sidebarOpen ? DRAWER_WIDTH : 0) - 2 * WINDOW_MARGIN), 1180);
            const height = currentSize?.height ?? Math.max(WINDOW_MIN_HEIGHT, window.innerHeight - WINDOW_TOP - FOOTER_HEIGHT - WINDOW_MARGIN);
            const x = Math.max(sidebarOpen ? DRAWER_WIDTH : 0, Math.min(window.innerWidth - width, event.clientX - drag.offsetX));
            const y = Math.max(56, Math.min(window.innerHeight - FOOTER_HEIGHT - height, event.clientY - drag.offsetY));
            setWindowPositions((prev) => ({ ...prev, [drag.id]: { x, y } }));
        };
        const onUp = () => { dragRef.current = null; resizeRef.current = null; document.body.style.userSelect = ''; };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
    }, [isMobile, sidebarOpen, windowPositions, windowSizes]);

    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                setGlobalSearchOpen(true);
                const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement;
                if (searchInput) searchInput.focus();
            }
            if (e.key === 'Escape') {
                if (globalSearchOpen) {
                    setGlobalSearchOpen(false);
                    setShowSearchResults(false);
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [globalSearchOpen]);

    React.useEffect(() => {
        if (globalSearchQuery.trim().length >= 2) {
            setShowSearchResults(true);
        } else {
            setShowSearchResults(false);
        }
    }, [globalSearchQuery]);

    const loadCompanies = React.useCallback(async () => {
        try {
            let list: Array<{ CompanyID: number; CompanyName: string }> = [];
            try {
                const resUser = await apiService.getMyCompanies(true);
                const rawUser = Array.isArray(resUser) ? resUser : (resUser as any)?.data || [];
                list = rawUser.map((c: any) => ({ CompanyID: c.CompanyID ?? c.companyid, CompanyName: c.CompanyName ?? c.companyname, DatabaseName: c.DatabaseName ?? c.databasename }));
            } catch {}
            if (!list.length) {
                try {
                    const resAll = await apiService.getCompanies(true);
                    const rawAll = Array.isArray(resAll) ? resAll : (resAll as any)?.data || [];
                    list = rawAll.map((c: any) => ({ CompanyID: c.CompanyID ?? c.companyid, CompanyName: c.CompanyName ?? c.companyname, DatabaseName: c.DatabaseName ?? c.databasename }));
                } catch {}
            }
            setCompanies(list);
            try {
                const ids = new Set(list.map((c) => c.CompanyID));
                const saved = Number(localStorage.getItem('selectedCompanyId') || '');
                const effective = (saved || selectedCompanyId) as any;
                if (effective && !ids.has(Number(effective))) {
                    setSelectedCompanyId('');
                    try { localStorage.removeItem('selectedCompanyId'); } catch {}
                }
            } catch {}
        } catch (e: any) {
            dispatch(showSnackbar({ message: e?.response?.data?.message || 'Failed to load companies', severity: 'error' }));
        }
    }, [dispatch, selectedCompanyId]);

    React.useEffect(() => { void loadCompanies(); }, [loadCompanies]);

    const handleLogout = () => {
        if (!confirmUnsavedChanges()) return;
        dispatch(logout());
        navigate('/login');
    };

    const handleCompanyChange = (companyId: string) => {
        if (companyId === String(selectedCompanyId)) return;

        if (companyLocked) {
            setPendingCompanyId(companyId);
            setShowCompanyChangeConfirm(true);
        } else {
            confirmCompanyChange(companyId);
        }
    };

    const confirmCompanyChange = (companyId: string) => {
        if (!confirmUnsavedChanges()) return;
        setSelectedCompanyId(Number(companyId));
        localStorage.setItem('selectedCompanyId', companyId);
        window.dispatchEvent(new CustomEvent('company-changed', { detail: { companyId } }));
        setCompanyLocked(true);
        setShowCompanyChangeConfirm(false);
        setPendingCompanyId(null);
    };

    const cancelCompanyChange = () => {
        setShowCompanyChangeConfirm(false);
        setPendingCompanyId(null);
    };

    const companySelected = !!selectedCompanyId;
    const forceChild = location.pathname.startsWith('/pr/') || location.pathname.startsWith('/po/');
    const [closingOverlay, setClosingOverlay] = React.useState(false);

    const openWindow = (key: string, title: string, path?: string) => {
        if (!companySelected) {
            dispatch(showSnackbar({ message: 'Please select a company to continue', severity: 'warning' }));
            return;
        }
        const existing = windows.find((w) => w.key === key);
        if (existing) {
            if (existing.minimized) {
                setWindows((prev) => prev.map((w) => w.id === existing.id ? { ...w, minimized: false } : w));
            }
            setActiveWindowId(existing.id);
        } else {
            const id = Date.now();
            setWindows((prev) => [...prev, { id, key, title, minimized: false }]);
            setActiveWindowId(id);
        }
        if (forceChild && path) {
            setClosingOverlay(true);
            setTimeout(() => { navigate(path); }, 150);
        }
    };

    const closeWindow = (id: number) => {
        const target = windows.find(w => w.id === id);
        const path = MODULES.find(m => m.key === target?.key)?.path;
        if (path && !confirmUnsavedChanges(path)) return;
        if (autoHiddenSidebarRef.current === id) {
            if (!sidebarOpen) dispatch(toggleSidebar());
            autoHiddenSidebarRef.current = null;
        }
        const remaining = windows.filter((w) => w.id !== id);
        setWindows(remaining);
        setActiveWindowId((prev) => prev === id
            ? (remaining.filter((w) => !w.minimized).slice(-1)[0]?.id ?? remaining.slice(-1)[0]?.id ?? null)
            : prev);
        setWindowPositions((prev) => { const next = { ...prev }; delete next[String(id)]; return next; });
        setWindowSizes((prev) => { const next = { ...prev }; delete next[String(id)]; return next; });
        setMaximizedWindows((prev) => { const next = { ...prev }; delete next[String(id)]; return next; });
    };

    const minimizeWindow = (id: number) => {
        if (autoHiddenSidebarRef.current === id) {
            if (!sidebarOpen) dispatch(toggleSidebar());
            autoHiddenSidebarRef.current = null;
        }
        setWindows((prev) => prev.map((w) => (w.id === id ? { ...w, minimized: true } : w)));
        if (activeWindowId === id) {
            const next = windows.filter((w) => w.id !== id && !w.minimized).slice(-1)[0];
            setActiveWindowId(next?.id ?? null);
        }
    };

    const restoreWindow = (id: number) => {
        setWindows((prev) => prev.map((w) => (w.id === id ? { ...w, minimized: false } : w)));
        setActiveWindowId(id);
    };

    const startWindowDrag = (event: React.MouseEvent, id: number) => {
        if (isMobile || event.button !== 0 || maximizedWindows[String(id)]) return;
        const current = windowPositions[String(id)] || { x: WINDOW_MARGIN, y: WINDOW_TOP };
        dragRef.current = { id, offsetX: event.clientX - current.x, offsetY: event.clientY - current.y };
        setActiveWindowId(id);
        document.body.style.userSelect = 'none';
    };

    const startWindowResize = (event: React.MouseEvent, id: number, width: number, height: number, direction: string, position: { x: number; y: number }) => {
        if (isMobile || event.button !== 0 || maximizedWindows[String(id)]) return;
        event.stopPropagation();
        resizeRef.current = { id, startX: event.clientX, startY: event.clientY, startWidth: width, startHeight: height, startLeft: position.x, startTop: position.y, direction };
        setActiveWindowId(id);
        document.body.style.userSelect = 'none';
    };

    const toggleMaximized = (id: number) => {
        setActiveWindowId(id);
        const isCurrentlyMaximized = !!maximizedWindows[String(id)];
        if (!isCurrentlyMaximized) {
            if (sidebarOpen) {
                dispatch(toggleSidebar());
                autoHiddenSidebarRef.current = id;
            }
        } else if (autoHiddenSidebarRef.current === id) {
            if (!sidebarOpen) dispatch(toggleSidebar());
            autoHiddenSidebarRef.current = null;
        }
        setMaximizedWindows((prev) => ({ ...prev, [String(id)]: !isCurrentlyMaximized }));
    };

    const adjustWindowSize = (id: number, width: number, height: number, direction: -1 | 1) => {
        if (isMobile || maximizedWindows[String(id)]) return;
        const sidebarOffset = sidebarOpen ? DRAWER_WIDTH : 0;
        const maxWidth = Math.max(WINDOW_MIN_WIDTH, window.innerWidth - sidebarOffset - WINDOW_MARGIN * 2);
        const maxHeight = Math.max(WINDOW_MIN_HEIGHT, window.innerHeight - WINDOW_TOP - FOOTER_HEIGHT - WINDOW_MARGIN);
        const nextWidth = Math.max(WINDOW_MIN_WIDTH, Math.min(maxWidth, width + 140 * direction));
        const nextHeight = Math.max(WINDOW_MIN_HEIGHT, Math.min(maxHeight, height + 90 * direction));
        const current = windowPositions[String(id)] || { x: sidebarOffset + WINDOW_MARGIN, y: WINDOW_TOP };
        const centerX = current.x + width / 2;
        const centerY = current.y + height / 2;
        setWindowSizes((prev) => ({ ...prev, [String(id)]: { width: nextWidth, height: nextHeight } }));
        setWindowPositions((prev) => ({
            ...prev,
            [String(id)]: {
                x: Math.max(sidebarOffset, Math.min(window.innerWidth - nextWidth, centerX - nextWidth / 2)),
                y: Math.max(56, Math.min(window.innerHeight - FOOTER_HEIGHT - nextHeight, centerY - nextHeight / 2)),
            },
        }));
    };

    React.useEffect(() => { try { localStorage.setItem(WINDOWS_KEY, JSON.stringify(windows)); } catch {} }, [windows]);
    React.useEffect(() => { try { localStorage.setItem(ACTIVE_WINDOW_KEY, String(activeWindowId ?? '')); } catch {} }, [activeWindowId]);
    React.useEffect(() => { try { localStorage.setItem('app_window_positions_v1', JSON.stringify(windowPositions)); } catch {} }, [windowPositions]);
    React.useEffect(() => { try { localStorage.setItem('app_window_sizes_v1', JSON.stringify(windowSizes)); } catch {} }, [windowSizes]);
    React.useEffect(() => { try { localStorage.setItem('app_window_maximized_v1', JSON.stringify(maximizedWindows)); } catch {} }, [maximizedWindows]);
    React.useEffect(() => {
        if (!location.pathname.startsWith('/pr/') && !location.pathname.startsWith('/po/')) {
            setClosingOverlay(false);
        }
    }, [location.pathname]);

    // Refresh user profile (including allowed modules) when company changes
    React.useEffect(() => {
        const handleCompanyChange = (e: any) => {
            const newCompanyId = e.detail?.companyId;
            if (newCompanyId) {
                setSelectedCompanyId(Number(newCompanyId));
            }
            dispatch(getProfile() as any);
        };
        window.addEventListener('company-changed', handleCompanyChange);
        return () => window.removeEventListener('company-changed', handleCompanyChange);
    }, [dispatch]);

    const allowedModules: string[] = ((user as any)?.allowedmodules ?? []) as string[];
    const userRoles = (user?.roles ?? [user?.role || '']).map(r => r.trim().toLowerCase());
    const isAdmin = userRoles.includes('admin');
    const selectedCompanyName = React.useMemo(() => {
        const id = Number(selectedCompanyId || 0);
        if (!id) return '';
        const found = companies.find((c) => Number(c.CompanyID) === id);
        return found ? String(found.CompanyName || '') : '';
    }, [companies, selectedCompanyId]);
    const selectedDatabaseName = React.useMemo(() => {
        const id = Number(selectedCompanyId || 0);
        if (!id) return '';
        const found = companies.find((c) => Number(c.CompanyID) === id);
        return found ? String(found.DatabaseName || '') : '';
    }, [companies, selectedCompanyId]);

    // Compute menu from registry with unified role + module checks
    const canSee = (item: typeof MODULES[number]) => {
        const roleOk = isAdmin ? true : !item.roles || item.roles.length === 0 || item.roles.some((r) => userRoles.includes(r.toLowerCase()));
        const moduleOk = isAdmin ? true : !item.moduleKey || item.moduleKey === 'dashboard' || allowedModules.includes(item.moduleKey);
        return roleOk && moduleOk && !item.hideFromMenu;
    };

    // Build grouped menu: show groups with permitted children; also show single items not part of any group
    const groupedKeys = new Set<string>(MENU_GROUPS.flatMap((g) => g.children.map((c) => c.key)));
    const singleItems = MODULES
        .filter(canSee)
        .filter((item) => !groupedKeys.has(item.key))
        .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
    const menuGroups = MENU_GROUPS
        .map((g) => ({
            ...g,
            children: g.children.filter(canSee).sort((a, b) => (a.order ?? 100) - (b.order ?? 100)),
        }))
        .filter((g) => g.children.length > 0)
        .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));

    // Separate groups to control rendering order (Dashboard first, then Workflow, then Settings)
    const dashboardGroup = menuGroups.find((g) => g.key === 'group-dashboard');
    const workGroup = menuGroups.find((g) => g.key === 'group-work');
    const settingsGroup = menuGroups.find((g) => g.key === 'group-settings');
    const otherGroups = menuGroups.filter((g) => g.key !== 'group-dashboard' && g.key !== 'group-work' && g.key !== 'group-settings');

    // Default all groups to collapsed; expand only when user clicks
    const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(
        MENU_GROUPS.reduce((acc, g) => {
            acc[g.key] = isAdmin && g.key === 'group-settings' ? true : false;
            return acc;
        }, {} as Record<string, boolean>)
    );
    const toggleGroup = (key: string) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

    React.useEffect(() => {
        const grantAllIfAdminRole = async () => {
            try {
                const isAdminRole = String(user?.role || '').toLowerCase() === 'admin';
                if (!user?.userid || !isAdminRole) return;
                try { await apiService.grantAllUserModules(user.userid); } catch {}
                try { await apiService.grantAllUserCompanies(user.userid); } catch {}
                await loadCompanies();
            } catch {}
        };
        grantAllIfAdminRole();
    }, [user?.userid, user?.role, loadCompanies]);

    React.useEffect(() => {
        if (windows.length > 0) {
            const exists = windows.some((w) => w.id === activeWindowId);
            if (!exists) {
                const target = windows.find((w) => !w.minimized) ?? windows[0];
                if (target) setActiveWindowId(target.id);
            }
        }
    }, [windows, activeWindowId]);

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: (theme) => theme.palette.background.paper }}>
            <AppBar
                position="fixed"
                color="primary"
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.12)',
                    backgroundColor: '#0a6ed1',
                    color: '#fff',
                    height: 56,
                }}
            >
                <Toolbar
                    sx={{
                        minHeight: '56px !important',
                        height: 56,
                        px: isMobile ? 1 : 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    {/* Left Section: Menu + Company Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            color="inherit"
                            edge="start"
                            onClick={() => dispatch(toggleSidebar())}
                            sx={{
                                padding: 8,
                                width: 36,
                                height: 36,
                                borderRadius: 1,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                }
                            }}
                        >
                            <MenuIcon sx={{ fontSize: 20 }} />
                        </IconButton>

                        {/* Company Selection - Compact */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 1.5,
                            py: 0.75,
                            backgroundColor: 'rgba(255,255,255,0.08)',
                            borderRadius: 1.5,
                            border: '1px solid rgba(255,255,255,0.15)',
                        }}>
                            <BusinessIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.9)' }} />

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        color: 'rgba(255,255,255,0.7)',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        fontFamily: '"Arial", "Helvetica", sans-serif',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {selectedCompanyName || 'Select Company'}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '11px',
                                        fontWeight: 400,
                                        color: 'rgba(255,255,255,0.8)',
                                        fontFamily: '"Consolas", "Monaco", monospace',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {selectedDatabaseName || '—'}
                                </Typography>
                            </Box>

                            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: 0.5 }} />

                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel
                                    id="app-company-select-label"
                                    sx={{
                                        color: 'rgba(255,255,255,0.7)',
                                        fontSize: '12px',
                                        fontFamily: '"Arial", "Helvetica", sans-serif',
                                        fontWeight: 500,
                                        '&.Mui-focused': {
                                            color: 'rgba(255,255,255,0.9)',
                                        },
                                    }}
                                >
                                    Change
                                </InputLabel>
                                <Select
                                    labelId="app-company-select-label"
                                    id="app-company-select"
                                    value={selectedCompanyId !== '' && companies.some((company) => Number(company.CompanyID) === Number(selectedCompanyId)) ? String(selectedCompanyId) : ''}
                                    label="Change"
                                    onChange={(e) => handleCompanyChange(e.target.value as string)}
                                    disabled={companyLocked}
                                    sx={{
                                        color: '#fff',
                                        fontSize: '12px',
                                        fontFamily: '"Arial", "Helvetica", sans-serif',
                                        backgroundColor: 'rgba(255,255,255,0.08)',
                                        '& .MuiSelect-select': {
                                            backgroundColor: 'transparent',
                                            py: 0.75,
                                        },
                                        '&.Mui-disabled': {
                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                            color: 'rgba(255,255,255,0.5)',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(255,255,255,0.3)',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(255,255,255,0.5)',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(255,255,255,0.7)',
                                        },
                                    }}
                                >
                                    <MenuItem value="" sx={{ fontSize: '12px', fontFamily: '"Arial", "Helvetica", sans-serif' }}>
                                        <em>Select...</em>
                                    </MenuItem>
                                    {companies.map((c: any) => (
                                        <MenuItem
                                            key={c.CompanyID}
                                            value={String(c.CompanyID)}
                                            sx={{
                                                fontSize: '12px',
                                                fontFamily: '"Arial", "Helvetica", sans-serif',
                                                py: 0.75,
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                <Box sx={{
                                                    fontWeight: 600,
                                                    color: '#0a6ed1',
                                                    minWidth: 30,
                                                    fontSize: '11px',
                                                }}>
                                                    {c.CompanyID}
                                                </Box>
                                                <Box sx={{ fontWeight: 400 }}>
                                                    {c.CompanyName || c.companyname}
                                                </Box>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    {/* Center Section: Search */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1,
                        maxWidth: 400,
                        mx: 2,
                    }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder={globalSearchOpen ? 'Search...' : 'Search... (Ctrl+K)'}
                            value={globalSearchQuery}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGlobalSearchQuery(e.target.value)}
                            onFocus={() => setGlobalSearchOpen(true)}
                            onBlur={() => setTimeout(() => setGlobalSearchOpen(false), 200)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'rgba(255,255,255,0.12)',
                                    borderRadius: 2,
                                    height: 36,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '& fieldset': {
                                        borderColor: 'transparent',
                                        borderWidth: 1,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(255,255,255,0.3)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgba(255,255,255,0.5)',
                                        backgroundColor: 'rgba(255,255,255,0.18)',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    color: '#fff',
                                    fontSize: 13,
                                    fontFamily: '"Arial", "Helvetica", sans-serif',
                                    '&::placeholder': {
                                        color: 'rgba(255,255,255,0.6)',
                                        fontSize: 13,
                                        opacity: 0.7,
                                    },
                                },
                            }}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, fontSize: 18, color: 'rgba(255,255,255,0.7)' }} />,
                            }}
                        />
                    </Box>

                    {/* Right Section: Lock + User + Logout */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {/* Lock/Unlock Button */}
                        <Tooltip
                            title={companyLocked ? "Company locked - Click to unlock" : "Company unlocked - Click to lock"}
                            arrow
                            placement="bottom"
                        >
                            <Button
                                size="small"
                                variant={companyLocked ? "contained" : "outlined"}
                                startIcon={companyLocked ? <Lock sx={{ fontSize: 15 }} /> : <LockOpen sx={{ fontSize: 15 }} />}
                                onClick={() => setCompanyLocked((v) => !v)}
                                sx={{
                                    borderColor: companyLocked ? 'transparent' : 'rgba(255,255,255,0.3)',
                                    color: '#fff',
                                    backgroundColor: companyLocked ? 'rgba(244, 67, 54, 0.9)' : 'rgba(255,255,255,0.05)',
                                    fontFamily: '"Arial", "Helvetica", sans-serif',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    minHeight: 32,
                                    py: 0.6,
                                    px: 1.25,
                                    borderRadius: 1.5,
                                    letterSpacing: '0.25px',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: 'rgba(255,255,255,0.5)',
                                        backgroundColor: companyLocked ? 'rgba(211, 47, 47, 1)' : 'rgba(255,255,255,0.15)',
                                        transform: 'translateY(-1px)',
                                    },
                                    '&:active': {
                                        transform: 'translateY(0)',
                                    },
                                }}
                            >
                                {companyLocked ? 'Locked' : 'Unlock'}
                            </Button>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', height: 24 }} />

                        {/* User Info */}
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '13px',
                                fontWeight: 500,
                                fontFamily: '"Arial", "Helvetica", sans-serif',
                                color: 'rgba(255,255,255,0.95)',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {user?.fullname}
                        </Typography>

                        {/* Logout Button */}
                        <Button
                            variant="outlined"
                            onClick={handleLogout}
                            startIcon={<Logout sx={{ fontSize: 14 }} />}
                            sx={{
                                borderColor: 'rgba(255,255,255,0.3)',
                                color: '#fff',
                                backgroundColor: 'transparent',
                                fontFamily: '"Arial", "Helvetica", sans-serif',
                                fontWeight: 600,
                                minHeight: 32,
                                py: 0.6,
                                px: 1.25,
                                fontSize: '12px',
                                textTransform: 'none',
                                borderRadius: 1.5,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: 'rgba(255,255,255,0.5)',
                                    backgroundColor: 'rgba(255,255,255,0.15)',
                                }
                            }}
                        >
                            Logout
                        </Button>
                    </Box>
                </Toolbar>
            </AppBar>

            <Drawer
                variant={isMobile ? 'temporary' : 'persistent'}
                open={sidebarOpen}
                sx={{
                    width: isMobile ? 280 : DRAWER_WIDTH,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: isMobile ? 280 : DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(247, 247, 247, 0.98) 100%)',
                        backdropFilter: 'blur(20px)',
                        borderRight: '1px solid rgba(255, 255, 255, 0.6)',
                        boxShadow: '4px 0 24px rgba(0, 0, 0, 0.06)',
                    },
                }}
            >
                <Toolbar sx={{ minHeight: '48px !important', height: 48 }} />
                <Box sx={{ overflow: 'auto', px: 1, pb: 2 }}>
                    <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        {/* 1) Dashboard group at top */}
                        {dashboardGroup && (() => {
                            const isOpen = !!openGroups[dashboardGroup.key];
                            return (
                                <Box key={dashboardGroup.key}>
                                    <Box
                                        onClick={() => toggleGroup(dashboardGroup.key)}
                                        sx={{
                                            py: 0.75,
                                            px: 1,
                                            borderRadius: 0.5,
                                            background: isOpen
                                                ? 'rgba(10, 110, 209, 0.08)'
                                                : 'transparent',
                                            border: isOpen ? '1px solid rgba(10, 110, 209, 0.15)' : '1px solid transparent',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 24,
                                                height: 24,
                                                borderRadius: 0.25,
                                                background: isOpen ? '#0a6ed1' : 'transparent',
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            <ExpandMore
                                                sx={{
                                                    fontSize: 14,
                                                    color: isOpen ? '#fff' : '#666',
                                                    transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                                    transition: 'transform 0.2s ease',
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: '#333',
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.5,
                                            }}
                                        >
                                            {dashboardGroup.label}
                                        </Typography>
                                    </Box>
                                    <Collapse in={isOpen} timeout={200}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.125, mt: 0.25 }}>
                                            {dashboardGroup.children.map((child) => {
                                                const selected = location.pathname === child.path;
                                                return (
                                                    <Box
                                                        key={child.key}
                                                        onClick={() => openWindow(child.key, child.label, child.path)}
                                                        sx={{
                                                            py: 0.5,
                                                            px: 1,
                                                            ml: 1.5,
                                                            borderRadius: 0.25,
                                                            background: selected ? '#0a6ed1' : 'transparent',
                                                            border: selected ? 'none' : '1px solid transparent',
                                                            opacity: companySelected ? 1 : 0.3,
                                                            transition: 'all 0.15s ease',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.75,
                                                            cursor: 'pointer',
                                                            '&:hover': companySelected ? {
                                                                background: selected ? '#085bb0' : 'rgba(0, 0, 0, 0.04)',
                                                            } : {},
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: 20,
                                                                height: 20,
                                                            }}
                                                        >
                                                            {React.cloneElement(child.icon as React.ReactElement, {
                                                                sx: {
                                                                    fontSize: 16,
                                                                    color: selected ? '#fff' : '#0a6ed1',
                                                                }
                                                            })}
                                                        </Box>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 12,
                                                                fontWeight: selected ? 600 : 400,
                                                                color: selected ? '#fff' : '#333',
                                                            }}
                                                        >
                                                            {child.label}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })()}

                        {/* 2) Work Flow group */}
                        {workGroup && (() => {
                            const isOpen = !!openGroups[workGroup.key];
                            return (
                                <Box key={workGroup.key}>
                                    <Box
                                        onClick={() => toggleGroup(workGroup.key)}
                                        sx={{
                                            py: 0.75,
                                            px: 1,
                                            borderRadius: 0.5,
                                            background: isOpen
                                                ? 'rgba(10, 110, 209, 0.08)'
                                                : 'transparent',
                                            border: isOpen ? '1px solid rgba(10, 110, 209, 0.15)' : '1px solid transparent',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 24,
                                                height: 24,
                                                borderRadius: 0.25,
                                                background: isOpen ? '#0a6ed1' : 'transparent',
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            <ExpandMore
                                                sx={{
                                                    fontSize: 14,
                                                    color: isOpen ? '#fff' : '#666',
                                                    transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                                    transition: 'transform 0.2s ease',
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: '#333',
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.5,
                                            }}
                                        >
                                            {workGroup.label}
                                        </Typography>
                                    </Box>
                                    <Collapse in={isOpen} timeout={200}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.125, mt: 0.25 }}>
                                            {workGroup.children.map((child) => {
                                                const selected = location.pathname === child.path;
                                                return (
                                                    <Box
                                                        key={child.key}
                                                        onClick={() => openWindow(child.key, child.label, child.path)}
                                                        sx={{
                                                            py: 0.5,
                                                            px: 1,
                                                            ml: 1.5,
                                                            borderRadius: 0.25,
                                                            background: selected ? '#0a6ed1' : 'transparent',
                                                            border: selected ? 'none' : '1px solid transparent',
                                                            opacity: companySelected ? 1 : 0.3,
                                                            transition: 'all 0.15s ease',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.75,
                                                            cursor: 'pointer',
                                                            '&:hover': companySelected ? {
                                                                background: selected ? '#085bb0' : 'rgba(0, 0, 0, 0.04)',
                                                            } : {},
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: 20,
                                                                height: 20,
                                                            }}
                                                        >
                                                            {React.cloneElement(child.icon as React.ReactElement, {
                                                                sx: {
                                                                    fontSize: 16,
                                                                    color: selected ? '#fff' : '#0a6ed1',
                                                                }
                                                            })}
                                                        </Box>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 12,
                                                                fontWeight: selected ? 600 : 400,
                                                                color: selected ? '#fff' : '#333',
                                                            }}
                                                        >
                                                            {child.label}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })()}

                        {/* 2) Single items in the middle */}
                        {singleItems.map((item) => {
                            const selected = location.pathname === item.path;
                            return (
                                <Box
                                    key={item.key}
                                    onClick={() => openWindow(item.key, item.label, item.path)}
                                    sx={{
                                        py: 0.5,
                                        px: 1,
                                        borderRadius: 0.25,
                                        background: selected ? '#0a6ed1' : 'transparent',
                                        border: selected ? 'none' : '1px solid transparent',
                                        opacity: companySelected ? 1 : 0.3,
                                        transition: 'all 0.15s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        cursor: 'pointer',
                                        '&:hover': companySelected ? {
                                            background: selected ? '#085bb0' : 'rgba(0, 0, 0, 0.04)',
                                        } : {},
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 20,
                                            height: 20,
                                        }}
                                    >
                                        {React.cloneElement(item.icon as React.ReactElement, {
                                            sx: {
                                                fontSize: 16,
                                                color: selected ? '#fff' : '#0a6ed1',
                                            }
                                        })}
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: selected ? 600 : 400,
                                            color: selected ? '#fff' : '#333',
                                        }}
                                    >
                                        {item.label}
                                    </Typography>
                                </Box>
                            );
                        })}

                        {/* 3) Any other groups next */}
                        {otherGroups.map((group) => {
                            const isOpen = !!openGroups[group.key];
                            return (
                                <Box key={group.key}>
                                    <Box
                                        onClick={() => toggleGroup(group.key)}
                                        sx={{
                                            py: 0.75,
                                            px: 1,
                                            borderRadius: 0.5,
                                            background: isOpen
                                                ? 'rgba(10, 110, 209, 0.08)'
                                                : 'transparent',
                                            border: isOpen ? '1px solid rgba(10, 110, 209, 0.15)' : '1px solid transparent',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 24,
                                                height: 24,
                                                borderRadius: 0.25,
                                                background: isOpen ? '#0a6ed1' : 'transparent',
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            <ExpandMore
                                                sx={{
                                                    fontSize: 14,
                                                    color: isOpen ? '#fff' : '#666',
                                                    transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                                    transition: 'transform 0.2s ease',
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: '#333',
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.5,
                                            }}
                                        >
                                            {group.label}
                                        </Typography>
                                    </Box>
                                    <Collapse in={isOpen} timeout={200}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.125, mt: 0.25 }}>
                                            {group.children.map((child) => {
                                                const selected = location.pathname === child.path;
                                                return (
                                                    <Box
                                                        key={child.key}
                                                        onClick={() => openWindow(child.key, child.label, child.path)}
                                                        sx={{
                                                            py: 0.5,
                                                            px: 1,
                                                            ml: 1.5,
                                                            borderRadius: 0.25,
                                                            background: selected ? '#0a6ed1' : 'transparent',
                                                            border: selected ? 'none' : '1px solid transparent',
                                                            opacity: companySelected ? 1 : 0.3,
                                                            transition: 'all 0.15s ease',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.75,
                                                            cursor: 'pointer',
                                                            '&:hover': companySelected ? {
                                                                background: selected ? '#085bb0' : 'rgba(0, 0, 0, 0.04)',
                                                            } : {},
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: 20,
                                                                height: 20,
                                                            }}
                                                        >
                                                            {React.cloneElement(child.icon as React.ReactElement, {
                                                                sx: {
                                                                    fontSize: 16,
                                                                    color: selected ? '#fff' : '#0a6ed1',
                                                                }
                                                            })}
                                                        </Box>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 12,
                                                                fontWeight: selected ? 600 : 400,
                                                                color: selected ? '#fff' : '#333',
                                                            }}
                                                        >
                                                            {child.label}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })}

                        {/* 4) Settings group last */}
                        {settingsGroup && (() => {
                            const isOpen = !!openGroups[settingsGroup.key];
                            return (
                                <Box key={settingsGroup.key}>
                                    <Box
                                        onClick={() => toggleGroup(settingsGroup.key)}
                                        sx={{
                                            py: 0.75,
                                            px: 1,
                                            borderRadius: 0.5,
                                            background: isOpen
                                                ? 'rgba(10, 110, 209, 0.08)'
                                                : 'transparent',
                                            border: isOpen ? '1px solid rgba(10, 110, 209, 0.15)' : '1px solid transparent',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 24,
                                                height: 24,
                                                borderRadius: 0.25,
                                                background: isOpen ? '#0a6ed1' : 'transparent',
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            <ExpandMore
                                                sx={{
                                                    fontSize: 14,
                                                    color: isOpen ? '#fff' : '#666',
                                                    transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                                    transition: 'transform 0.2s ease',
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: '#333',
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.5,
                                            }}
                                        >
                                            {settingsGroup.label}
                                        </Typography>
                                    </Box>
                                    <Collapse in={isOpen} timeout={200}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.125, mt: 0.25 }}>
                                            {settingsGroup.children.map((child) => {
                                                const selected = location.pathname === child.path;
                                                return (
                                                    <Box
                                                        key={child.key}
                                                        onClick={() => openWindow(child.key, child.label, child.path)}
                                                        sx={{
                                                            py: 0.5,
                                                            px: 1,
                                                            ml: 1.5,
                                                            borderRadius: 0.25,
                                                            background: selected ? '#0a6ed1' : 'transparent',
                                                            border: selected ? 'none' : '1px solid transparent',
                                                            opacity: companySelected ? 1 : 0.3,
                                                            transition: 'all 0.15s ease',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.75,
                                                            cursor: 'pointer',
                                                            '&:hover': companySelected ? {
                                                                background: selected ? '#085bb0' : 'rgba(0, 0, 0, 0.04)',
                                                            } : {},
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: 20,
                                                                height: 20,
                                                            }}
                                                        >
                                                            {React.cloneElement(child.icon as React.ReactElement, {
                                                                sx: {
                                                                    fontSize: 16,
                                                                    color: selected ? '#fff' : '#0a6ed1',
                                                                }
                                                            })}
                                                        </Box>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 12,
                                                                fontWeight: selected ? 600 : 400,
                                                                color: selected ? '#fff' : '#333',
                                                            }}
                                                        >
                                                            {child.label}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })()}
                    </List>
                </Box>
            </Drawer>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: isMobile ? 0 : 3,
                    pb: 0,
                    height: '100vh',
                    minHeight: 0,
                    overflow: 'hidden',
                    marginLeft: isMobile ? 0 : (sidebarOpen ? 0 : `-${DRAWER_WIDTH}px`),
                    transition: 'margin 0.3s',
                }}
            >
                <Toolbar sx={{ minHeight: isMobile ? '56px !important' : '48px !important', height: isMobile ? 56 : 48 }} />
                {windows.length > 0 && !forceChild && (
                    <Box sx={{ 
                        mb: 2,
                        px: 0,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                    }}>
                        {windows.map((w) => {
                            const isActive = w.id === activeWindowId;
                            return (
                                <Box
                                    key={w.id}
                                    onClick={() => setActiveWindowId(w.id)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.5,
                                        py: 0.6,
                                        borderRadius: 2,
                                        background: isActive
                                            ? 'linear-gradient(135deg, #0a6ed1 0%, #085bb0 100%)'
                                            : 'rgba(255, 255, 255, 0.7)',
                                        backdropFilter: isActive ? 'blur(20px)' : 'blur(10px)',
                                        border: isActive
                                            ? 'none'
                                            : '1px solid rgba(255, 255, 255, 0.8)',
                                        boxShadow: isActive
                                            ? '0 4px 16px rgba(10, 110, 209, 0.35), 0 2px 8px rgba(10, 110, 209, 0.2)'
                                            : '0 1px 4px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        position: 'relative',
                                        minHeight: 36,
                                        userSelect: 'none',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: isActive
                                                ? '0 6px 20px rgba(10, 110, 209, 0.4), 0 3px 12px rgba(10, 110, 209, 0.25)'
                                                : '0 3px 8px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
                                        },
                                        '&:active': {
                                            transform: 'translateY(-1px)',
                                        },
                                    }}
                                >
                                    <Typography 
                                        sx={{ 
                                            fontSize: 12,
                                            fontWeight: isActive ? 600 : 500,
                                            color: isActive ? '#fff' : '#2c3e50',
                                            letterSpacing: 0.1,
                                        }}
                                    >
                                        {w.title}
                                    </Typography>
                                    <Box 
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 0.25,
                                            ml: 0.25,
                                            opacity: 0,
                                            transition: 'opacity 0.2s ease',
                                        }}
                                        className="tab-actions"
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.opacity = '1';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.opacity = '0';
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                    >
                                        {w.minimized ? (
                                            <IconButton 
                                                component="span" 
                                                size="small" 
                                                onClick={(e) => { e.stopPropagation(); restoreWindow(w.id); }}
                                                sx={{ 
                                                    p: 0.25,
                                                    ml: 0.5,
                                                    color: isActive ? '#fff' : '#757575',
                                                    width: 20,
                                                    height: 20,
                                                    minWidth: 20,
                                                    '&:hover': {
                                                        backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
                                                        borderRadius: 1,
                                                    }
                                                }}
                                            >
                                                <Box sx={{ fontSize: 12, fontWeight: 600 }}>□</Box>
                                            </IconButton>
                                        ) : (
                                            <IconButton 
                                                component="span" 
                                                size="small" 
                                                onClick={(e) => { e.stopPropagation(); minimizeWindow(w.id); }}
                                                sx={{ 
                                                    p: 0.25,
                                                    color: isActive ? '#fff' : '#757575',
                                                    width: 20,
                                                    height: 20,
                                                    minWidth: 20,
                                                    '&:hover': {
                                                        backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
                                                        borderRadius: 1,
                                                    }
                                                }}
                                            >
                                                <Box sx={{ fontSize: 14, fontWeight: 300 }}>–</Box>
                                            </IconButton>
                                        )}
                                        <IconButton 
                                            component="span" 
                                            size="small" 
                                            onClick={(e) => { e.stopPropagation(); closeWindow(w.id); }}
                                            sx={{ 
                                                p: 0.25,
                                                color: isActive ? '#fff' : '#757575',
                                                width: 20,
                                                height: 20,
                                                minWidth: 20,
                                                '&:hover': {
                                                    backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
                                                    borderRadius: 1,
                                                }
                                            }}
                                        >
                                            <Box sx={{ fontSize: 16, fontWeight: 300 }}>×</Box>
                                        </IconButton>
                                    </Box>
                                    <style>{`
                                        [class*="tab-actions"]:hover {
                                            opacity: 1 !important;
                                        }
                                    `}</style>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {(windows.length === 0 || forceChild) && (
                    companySelected ? (
                        <Box sx={{ position: forceChild ? 'fixed' : 'relative', top: forceChild ? 56 : 'auto', right: forceChild ? 0 : 'auto', bottom: forceChild ? FOOTER_HEIGHT : 'auto', left: forceChild ? 0 : 'auto', height: forceChild ? `calc(100vh - 56px - ${FOOTER_HEIGHT}px)` : `calc(100vh - 56px)`, minHeight: 0, zIndex: forceChild ? (theme) => theme.zIndex.modal + 1 : 'auto', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'center', backgroundColor: forceChild ? 'rgba(0,0,0,0.08)' : 'transparent', overflow: 'hidden' }} aria-modal={forceChild ? true : undefined} role={forceChild ? 'dialog' : undefined}>
                            <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflowY: 'auto', overflowX: 'hidden', p: 0, pb: 2 }}>{children}</Box>
                            <Backdrop open={closingOverlay} sx={{ zIndex: (theme) => theme.zIndex.modal + 2, color: '#fff' }}>
                                <CircularProgress color="inherit" />
                            </Backdrop>
                        </Box>
                    ) : (
                        <Paper 
                            sx={{ 
                                p: 3, 
                                border: '1px dashed #cfd8dc',
                                borderRadius: 1.5,
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
                            }}
                        >
                            <Typography variant="h6" gutterBottom>Select a company to continue</Typography>
                            <Typography variant="body2">Use the Company dropdown in the top bar. Your selection will persist across refreshes.</Typography>
                        </Paper>
                    )
                )}

                {!forceChild && windows.filter((w) => !w.minimized && w.id === activeWindowId).map((w) => {
                    const mod = MODULES.find((m) => m.key === w.key);
                    if (!mod) return null;
                    const Comp = mod.component as any;
                    const isMaximized = !!maximizedWindows[String(w.id)];
                    const savedPosition = windowPositions[String(w.id)];
                    const savedSize = windowSizes[String(w.id)];
                    const defaultOffset = Math.max(0, (windows.findIndex((item) => item.id === w.id) % 5) * 24);
                    const sidebarOffset = sidebarOpen ? DRAWER_WIDTH : 0;
                    const availableWidth = Math.max(WINDOW_MIN_WIDTH, window.innerWidth - sidebarOffset - WINDOW_MARGIN * 2);
                    const availableHeight = Math.max(WINDOW_MIN_HEIGHT, window.innerHeight - WINDOW_TOP - FOOTER_HEIGHT - WINDOW_MARGIN);
                    const defaultWidth = Math.min(availableWidth, 1180);
                    const defaultHeight = availableHeight;
                    const windowWidth = Math.min(savedSize?.width ?? defaultWidth, availableWidth);
                    const windowHeight = Math.min(savedSize?.height ?? defaultHeight, availableHeight);
                    const normalLeft = Math.max(sidebarOffset, Math.min(window.innerWidth - windowWidth, savedPosition?.x ?? sidebarOffset + WINDOW_MARGIN + defaultOffset));
                    const normalTop = Math.max(56, Math.min(window.innerHeight - FOOTER_HEIGHT - windowHeight, savedPosition?.y ?? WINDOW_TOP + defaultOffset));
                    const resizeHandles = [
                        { direction: 'n', sx: { top: 0, left: 12, right: 12, height: 10, cursor: 'ns-resize' } },
                        { direction: 's', sx: { bottom: 0, left: 12, right: 12, height: 10, cursor: 'ns-resize' } },
                        { direction: 'w', sx: { left: 0, top: 12, bottom: 12, width: 10, cursor: 'ew-resize' } },
                        { direction: 'e', sx: { right: 0, top: 12, bottom: 12, width: 10, cursor: 'ew-resize' } },
                        { direction: 'nw', sx: { top: 0, left: 0, width: 14, height: 14, cursor: 'nwse-resize' } },
                        { direction: 'ne', sx: { top: 0, right: 0, width: 14, height: 14, cursor: 'nesw-resize' } },
                        { direction: 'sw', sx: { bottom: 0, left: 0, width: 14, height: 14, cursor: 'nesw-resize' } },
                        { direction: 'se', sx: { bottom: 0, right: 0, width: 14, height: 14, cursor: 'nwse-resize' } },
                    ];
                    return (
                        <Paper 
                            key={w.id} 
                            onMouseDown={() => setActiveWindowId(w.id)}
                            sx={{ 
                                position: isMobile ? 'fixed' : 'fixed',
                                left: isMobile ? 0 : (isMaximized ? sidebarOffset : normalLeft),
                                top: isMobile ? 56 : (isMaximized ? 56 : normalTop),
                                width: isMobile ? '100vw' : (isMaximized ? `calc(100vw - ${sidebarOpen ? DRAWER_WIDTH : 0}px)` : windowWidth),
                                height: isMobile ? `calc(100vh - 56px - ${FOOTER_HEIGHT}px)` : (isMaximized ? `calc(100vh - 56px - ${FOOTER_HEIGHT}px)` : Math.min(windowHeight, Math.max(WINDOW_MIN_HEIGHT, window.innerHeight - WINDOW_TOP - FOOTER_HEIGHT))),
                                maxWidth: '100vw',
                                maxHeight: `calc(100vh - 56px - ${FOOTER_HEIGHT}px)`,
                                border: isMobile ? 'none' : '1px solid #b8c7d1',
                                borderRadius: isMobile ? 0 : 1,
                                p: 0,
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                                zIndex: 1300,
                                boxShadow: isMobile ? 'none' : '0 8px 24px rgba(0, 0, 0, 0.22)',
                                background: '#ffffff',
                            }} 
                            variant="outlined"
                        >
                            <Box
                                onMouseDown={(event) => startWindowDrag(event, w.id)}
                                onDoubleClick={() => { if (!isMobile) toggleMaximized(w.id); }}
                                sx={{
                                    minHeight: 38,
                                    height: 38,
                                    px: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    color: '#fff',
                                    background: '#0a6ed1',
                                    cursor: isMobile || isMaximized ? 'default' : 'move',
                                    userSelect: 'none',
                                    touchAction: 'none',
                                }}
                            >
                                <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {w.title}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }} onMouseDown={(event) => event.stopPropagation()}>
                                    {!isMobile && (
                                        <Tooltip title={isMaximized ? 'Restore window' : 'Maximize window'}>
                                            <IconButton size="small" onClick={() => toggleMaximized(w.id)} sx={{ color: '#fff', p: 0.5 }}>
                                                {isMaximized ? <CropFreeIcon sx={{ fontSize: 17 }} /> : <OpenInFullIcon sx={{ fontSize: 17 }} />}
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {!isMobile && !isMaximized && (
                                        <>
                                            <Tooltip title="Decrease window size">
                                                <span><IconButton size="small" disabled={windowWidth <= WINDOW_MIN_WIDTH && windowHeight <= WINDOW_MIN_HEIGHT} onClick={() => adjustWindowSize(w.id, windowWidth, windowHeight, -1)} sx={{ color: '#fff', p: 0.5 }}>
                                                    <ZoomOutIcon sx={{ fontSize: 17 }} />
                                                </IconButton></span>
                                            </Tooltip>
                                            <Tooltip title="Increase window size">
                                                <span><IconButton size="small" disabled={windowWidth >= availableWidth && windowHeight >= availableHeight} onClick={() => adjustWindowSize(w.id, windowWidth, windowHeight, 1)} sx={{ color: '#fff', p: 0.5 }}>
                                                    <ZoomInIcon sx={{ fontSize: 17 }} />
                                                </IconButton></span>
                                            </Tooltip>
                                        </>
                                    )}
                                    {!isMobile && (
                                        <Tooltip title="Minimize window">
                                            <IconButton size="small" onClick={() => minimizeWindow(w.id)} sx={{ color: '#fff', p: 0.5 }}>
                                                <RemoveIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title={isMobile ? 'Back' : 'Close window'}>
                                        <IconButton size="small" onClick={() => closeWindow(w.id)} sx={{ color: '#fff', p: 0.5 }}>
                                            <CloseIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                            <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'auto', p: isMobile ? 0 : 1.5, pb: isMobile ? 2 : 3 }}>
                                <ModuleRoute moduleKey={mod.moduleKey} roles={mod.roles}><PageBoundary><React.Suspense fallback={<Typography role="status" sx={{ p: 2 }}>Loading page…</Typography>}><Comp /></React.Suspense></PageBoundary></ModuleRoute>
                            </Box>
                            {!isMobile && !isMaximized && resizeHandles.map((handle) => (
                                <Box
                                    key={handle.direction}
                                    onMouseDown={(event) => startWindowResize(event, w.id, windowWidth, windowHeight, handle.direction, { x: normalLeft, y: normalTop })}
                                    title="Resize window"
                                    sx={{
                                        position: 'absolute', zIndex: 2, borderRadius: 1,
                                        '&:hover': { backgroundColor: 'rgba(10,110,209,0.28)' },
                                        ...handle.sx,
                                    }}
                                />
                            ))}
                        </Paper>
                    );
                })}
                

                {!forceChild && windows.some((w) => w.minimized) && (
                    <Box sx={{
                        position: 'fixed',
                        bottom: FOOTER_HEIGHT,
                        left: isMobile ? 0 : (sidebarOpen ? DRAWER_WIDTH : 0),
                        right: 0,
                        p: isMobile ? 1 : 1.5,
                        borderTop: '1px solid rgba(255, 255, 255, 0.8)',
                        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(247, 247, 247, 0.98) 100%)',
                        backdropFilter: 'blur(20px)',
                        display: 'flex',
                        gap: 0.75,
                        flexWrap: 'wrap',
                        zIndex: 1000,
                        transition: 'left 0.3s',
                        maxHeight: isMobile ? 56 : 'none',
                        boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08), 0 -2px 8px rgba(0, 0, 0, 0.04)',
                    }}>
                        {windows.filter((w) => w.minimized).map((w) => (
                            <Chip
                                key={w.id}
                                label={w.title}
                                onClick={() => restoreWindow(w.id)}
                                onDelete={() => closeWindow(w.id)}
                                variant="outlined"
                                sx={{
                                    borderRadius: 1.25,
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(10, 110, 209, 0.2)',
                                    color: '#2c3e50',
                                    fontWeight: 500,
                                    fontSize: 12,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(10, 110, 209, 0.08)',
                                        borderColor: 'rgba(10, 110, 209, 0.3)',
                                        boxShadow: '0 2px 8px rgba(10, 110, 209, 0.15)',
                                        transform: 'translateY(-2px)',
                                    },
                                    '& .MuiChip-deleteIcon': {
                                        color: '#757575',
                                        fontSize: 16,
                                        '&:hover': {
                                            color: '#d32f2f',
                                        }
                                    }
                                }}
                            />
                         ))}
                     </Box>
                 )}
             </Box>

             <Box
                component="footer"
                sx={{
                    position: 'fixed',
                    left: isMobile ? 0 : (sidebarOpen ? DRAWER_WIDTH : 0),
                    right: 0,
                    bottom: 0,
                    height: FOOTER_HEIGHT,
                    px: isMobile ? 1 : 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderTop: '1px solid #c8d4da',
                    backgroundColor: '#eef2f4',
                    color: '#52656f',
                    zIndex: 1400,
                    transition: 'left 0.3s',
                }}
             >
                <Typography sx={{ fontSize: isMobile ? 10 : 11, color: '#52656f' }}>
                    {companySelected ? 'Application Ready' : 'Select Company'}
                </Typography>
                <Typography sx={{ fontSize: 10, color: '#70838d' }}>
                    {isMobile ? 'Online' : 'SAP B1 Workspace'}
                </Typography>
             </Box>

             <Dialog
                open={showCompanyChangeConfirm}
                onClose={cancelCompanyChange}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        border: '2px solid #f57c00',
                        boxShadow: '0 8px 32px rgba(245, 124, 0, 0.25)',
                    }
                }}
             >
                <DialogTitle sx={{
                    bgcolor: '#fff3e0',
                    color: '#e65100',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    borderBottom: '2px solid #f57c00',
                    py: 2,
                    px: 2.5,
                }}>
                    <WarningIcon sx={{ fontSize: 32, color: '#f57c00' }} />
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: '"Arial", "Helvetica", sans-serif', fontSize: '18px' }}>
                            Confirm Company Change
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: '"Arial", "Helvetica", sans-serif', fontSize: '12px', color: '#e65100', opacity: 0.8 }}>
                            This action will reload your permissions
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 2, px: 2.5 }}>
                    <Alert severity="warning" sx={{ mb: 3, borderRadius: 1, fontFamily: '"Arial", "Helvetica", sans-serif' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: '"Arial", "Helvetica", sans-serif' }}>
                            The company selection is currently locked for your safety
                        </Typography>
                    </Alert>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#616161',
                                    mb: 1,
                                    fontFamily: '"Arial", "Helvetica", sans-serif',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                Current Company
                            </Typography>
                            <Box sx={{
                                bgcolor: '#f5f5f5',
                                p: 2,
                                borderRadius: 1,
                                border: '2px solid #e0e0e0',
                            }}>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: '#0a6ed1', fontFamily: '"Arial", "Helvetica", sans-serif', fontSize: '15px' }}>
                                    {companies.find(c => c.CompanyID === Number(selectedCompanyId))?.CompanyName || 'Unknown'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#757575', fontFamily: '"Consolas", "Monaco", monospace', fontSize: '12px', mt: 0.5, display: 'block' }}>
                                    ID: {selectedCompanyId} | Database: {selectedDatabaseName || '—'}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1 }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                color: '#f57c00',
                            }}>
                                <Box sx={{ height: 2, flexGrow: 1, width: 40, bgcolor: '#f57c00' }} />
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#f57c00', fontFamily: '"Arial", "Helvetica", sans-serif', fontSize: '13px' }}>
                                    CHANGING TO
                                </Typography>
                                <Box sx={{ height: 2, flexGrow: 1, width: 40, bgcolor: '#f57c00' }} />
                            </Box>
                        </Box>

                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#616161',
                                    mb: 1,
                                    fontFamily: '"Arial", "Helvetica", sans-serif',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                New Company
                            </Typography>
                            <Box sx={{
                                bgcolor: '#e3f2fd',
                                p: 2,
                                borderRadius: 1,
                                border: '2px solid #2196f3',
                            }}>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1976d2', fontFamily: '"Arial", "Helvetica", sans-serif', fontSize: '15px' }}>
                                    {companies.find(c => String(c.CompanyID) === pendingCompanyId)?.CompanyName || 'Unknown'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#1976d2', fontFamily: '"Consolas", "Monaco", monospace', fontSize: '12px', mt: 0.5, display: 'block' }}>
                                    ID: {pendingCompanyId} | Database: {companies.find(c => String(c.CompanyID) === pendingCompanyId)?.DatabaseName || '—'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ mt: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc80' }}>
                        <Typography variant="body2" sx={{ color: '#e65100', fontFamily: '"Arial", "Helvetica", sans-serif', fontSize: '13px', fontWeight: 500 }}>
                            <strong>Important:</strong> This action will reload your user permissions and may affect your current work. All unsaved changes may be lost.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5, justifyContent: 'center' }}>
                    <Button
                        onClick={cancelCompanyChange}
                        variant="outlined"
                        sx={{
                            borderColor: '#bdbdbd',
                            color: '#616161',
                            backgroundColor: '#f5f5f5',
                            fontFamily: '"Arial", "Helvetica", sans-serif',
                            fontSize: '13px',
                            fontWeight: 600,
                            textTransform: 'none',
                            py: 1,
                            px: 3,
                            minWidth: 120,
                            borderRadius: 1.5,
                            '&:hover': {
                                borderColor: '#9e9e9e',
                                bgcolor: '#eeeeee',
                            }
                        }}
                    >
                        No, Cancel
                    </Button>
                    <Button
                        onClick={() => pendingCompanyId && confirmCompanyChange(pendingCompanyId)}
                        variant="contained"
                        sx={{
                            bgcolor: '#f57c00',
                            fontFamily: '"Arial", "Helvetica", sans-serif',
                            fontSize: '13px',
                            fontWeight: 700,
                            textTransform: 'none',
                            py: 1,
                            px: 3,
                            minWidth: 120,
                            borderRadius: 1.5,
                            '&:hover': {
                                bgcolor: '#ef6c00',
                            },
                        }}
                        autoFocus
                    >
                        Yes, Change
                    </Button>
                </DialogActions>
             </Dialog>

             <SearchResults
                open={showSearchResults}
                query={globalSearchQuery}
                onClose={() => {
                    setShowSearchResults(false);
                    setGlobalSearchQuery('');
                }}
             />
         </Box>
     );
};

export default MainLayout;
